import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { RichTextEditor } from '@/shared/components/custom/rich-text-editor';
import { useTaskCommentMutations } from '../../hooks/useTaskCommentMutations';

interface CommentComposerProps {
  taskId: string;
  parentId?: string | null;
  replyToAuthorName?: string | null;
  onCancelReply?: () => void;
}

export function CommentComposer({
  taskId,
  parentId,
  replyToAuthorName,
  onCancelReply,
}: CommentComposerProps) {
  const { t } = useTranslation('tasks');
  const [value, setValue] = useState('');
  const { addComment } = useTaskCommentMutations(taskId);

  const isEmpty = !value || value === '{"type":"doc","content":[{"type":"paragraph"}]}';

  async function handleSend() {
    if (isEmpty) return;
    try {
      await addComment.mutateAsync({ content: value, parentId: parentId ?? null });
      setValue('');
      onCancelReply?.();
    } catch {
      // handled by global error handler
    }
  }

  return (
    <div className="border-t p-3" data-testid="tasks-activity-comment-composer">
      {replyToAuthorName && (
        <div
          className="mb-2 flex items-center justify-between rounded-md bg-muted/40 px-2 py-1"
          data-testid="tasks-comment-reply-context"
        >
          <span className="text-xs text-muted-foreground">
            {t('activity.comment.replyingTo', { name: replyToAuthorName })}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4"
            aria-label={t('activity.comment.cancelReply')}
            data-testid="tasks-comment-reply-cancel"
            onClick={onCancelReply}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      <RichTextEditor
        value={value}
        onChange={setValue}
        placeholder={t('activity.comment.placeholder')}
        variant="comment"
        enableFixedToolbar
        enableFloatingMenu={false}
        enableBubbleMenu
        className="min-h-[88px]"
      />

      <div className="mt-2 flex justify-end">
        <Button
          size="sm"
          disabled={isEmpty || addComment.isPending}
          aria-label={t('activity.comment.sendAria')}
          data-testid="tasks-activity-comment-send"
          onClick={handleSend}
        >
          <Send className="mr-1.5 h-3.5 w-3.5" />
          {t('activity.comment.send')}
        </Button>
      </div>
    </div>
  );
}
